import { IncomeType } from '@zg-rentals/ts-capps-client';
import type { ApplicationConfigurationRequiredDocumentType } from '@zg-rentals/ts-capps-client';
import type { Answers, CurrentIncome, Document, RequiredDocumentSection } from '../models/rental-application';
import {
  APPLICATION_LEVEL_DOCUMENT_TYPES,
  INCOME_DOCUMENT_TYPE_ORDER,
  INCOME_LEVEL_DOCUMENT_TYPES,
  UNNAMED_INCOME_SOURCE,
} from '../constants/incomeDocs';
import type { IncomeDocumentSection } from '../hooks/useRequiredDocumentSections';
import { isIncomeDocumentSection, isRequiredSection } from '../hooks/useRequiredDocumentSections';

export type IncomeDocumentsMode = 'default' | 'template';

export type IncomeDocumentsAnswers = Pick<Answers, 'currentIncomes' | 'requiredDocuments'>;

export type RequestedRow = {
  documentType: ApplicationConfigurationRequiredDocumentType;
  section: RequiredDocumentSection;
  documents: Array<Document>;
};

export type IncomeSourceGroup = {
  income: CurrentIncome;
  index: number;
  label: string;
  requestedRows: Array<RequestedRow>;
  supportingDocuments: Array<Document>;
};

export type IncomeDocumentsModel = {
  mode: IncomeDocumentsMode;
  incomeSources: Array<IncomeSourceGroup>;
  applicationRows: Array<RequestedRow>;
  noLongerRequested: Array<RequestedRow>;
  requestedCount: number;
  coveredCount: number;
};

export type PendingRemoval =
  | {
      kind: 'detach';
      documentId: string;
      documentType: ApplicationConfigurationRequiredDocumentType;
      incomeId?: string;
    }
  | { kind: 'delete'; documentId: string };

type IndexedIncome = { income: CurrentIncome; index: number };

export function getIncomeSourceLabel(income: CurrentIncome, index: number) {
  const organization = income.type === IncomeType.EMPLOYMENT ? income.employer?.organization?.trim() : undefined;

  return organization || income.title?.trim() || `${UNNAMED_INCOME_SOURCE} #${index + 1}`;
}

function getDocuments(documents: Array<Document | null> | null | undefined): Array<Document> {
  return (documents ?? []).filter((document): document is Document => !!document);
}

function toRow(section: IncomeDocumentSection): RequestedRow {
  return { documentType: section.documentType, section, documents: getDocuments(section.documents) };
}

function getIncomes(answers: IncomeDocumentsAnswers | null | undefined): Array<IndexedIncome> {
  return (answers?.currentIncomes ?? []).flatMap((income, index) => (income ? [{ income, index }] : []));
}

function getSections(answers: IncomeDocumentsAnswers | null | undefined) {
  return (answers?.requiredDocuments ?? []).filter(isIncomeDocumentSection);
}

function getVisibleRows(incomes: Array<IndexedIncome>, sections: Array<IncomeDocumentSection>) {
  const requested = sections.filter(isRequiredSection);
  const findRow = (documentType: ApplicationConfigurationRequiredDocumentType, incomeId: string | undefined) => {
    const section = requested.find((s) => s.documentType === documentType && (s.incomeId || undefined) === incomeId);
    return section ? [toRow(section)] : [];
  };

  const incomeRows = new Map<number, Array<RequestedRow>>();
  for (const { income, index } of incomes) {
    if (income.id && income.type) {
      const types = INCOME_LEVEL_DOCUMENT_TYPES[income.type] ?? [];
      incomeRows.set(
        index,
        types.flatMap((documentType) => findRow(documentType, income.id!)),
      );
    }
  }
  const applicationRows = APPLICATION_LEVEL_DOCUMENT_TYPES.flatMap((documentType) => findRow(documentType, undefined));

  return { incomeRows, applicationRows };
}

function isHiddenBy(removal: PendingRemoval, documentId: string | null | undefined, section?: RequiredDocumentSection) {
  if (!documentId || documentId !== removal.documentId) {
    return false;
  }

  return (
    removal.kind === 'delete' ||
    (section?.documentType === removal.documentType && (section.incomeId || undefined) === removal.incomeId)
  );
}

export function withoutPendingRemovals(
  answers: IncomeDocumentsAnswers | null | undefined,
  removals: Array<PendingRemoval>,
): IncomeDocumentsAnswers | null | undefined {
  if (!answers || removals.length === 0) {
    return answers;
  }
  const isShown = (document: Document | null, section?: RequiredDocumentSection) =>
    !removals.some((removal) => isHiddenBy(removal, document?.id, section));

  return {
    currentIncomes: answers.currentIncomes?.map(
      (income) => income && { ...income, documents: income.documents?.filter((document) => isShown(document)) },
    ),
    requiredDocuments: answers.requiredDocuments?.map((section) => ({
      ...section,
      documents: section.documents?.filter((document) => isShown(document, section)),
    })),
  };
}

export function isPendingRemovalListed(answers: IncomeDocumentsAnswers | null | undefined, removal: PendingRemoval) {
  return (
    (answers?.requiredDocuments ?? []).some((section) =>
      (section.documents ?? []).some((document) => isHiddenBy(removal, document.id, section)),
    ) ||
    (answers?.currentIncomes ?? []).some((income) =>
      (income?.documents ?? []).some((document) => isHiddenBy(removal, document?.id)),
    )
  );
}

export function hasVisibleRequestedRow(answers: IncomeDocumentsAnswers | null | undefined) {
  const { incomeRows, applicationRows } = getVisibleRows(getIncomes(answers), getSections(answers));

  return applicationRows.length > 0 || [...incomeRows.values()].some((rows) => rows.length > 0);
}

export function getIncomeDocumentsModel(
  answers: IncomeDocumentsAnswers | null | undefined,
  { isTemplateEligible }: { isTemplateEligible: boolean },
): IncomeDocumentsModel {
  const incomes = getIncomes(answers);
  const sections = getSections(answers);
  const { incomeRows, applicationRows } = getVisibleRows(incomes, sections);
  const visibleSections = new Set([...[...incomeRows.values()].flat(), ...applicationRows].map((row) => row.section));
  const isTemplate = isTemplateEligible && visibleSections.size > 0;

  const incomeIndexById = new Map(incomes.flatMap(({ income, index }) => (income.id ? [[income.id, index]] : [])));
  const incomeOrder = (row: RequestedRow) => incomeIndexById.get(row.section.incomeId ?? '') ?? Number.MAX_SAFE_INTEGER;
  const noLongerRequested = sections
    .filter((section) => !visibleSections.has(section) && getDocuments(section.documents).length > 0)
    .map(toRow)
    .sort(
      (a, b) =>
        incomeOrder(a) - incomeOrder(b) ||
        INCOME_DOCUMENT_TYPE_ORDER.indexOf(a.documentType) - INCOME_DOCUMENT_TYPE_ORDER.indexOf(b.documentType),
    );

  const applicationRowIds = applicationRows.flatMap((row) => row.documents.map((document) => document.id));

  const incomeSources = incomes.map(({ income, index }) => {
    const requestedRows = isTemplate ? (incomeRows.get(index) ?? []) : [];
    // In template mode, a file in any section of the income, or on an application-level row, shows only there
    const rowDocumentIds = new Set(
      isTemplate
        ? [
            ...sections
              .filter((section) => section.incomeId === income.id)
              .flatMap((section) => getDocuments(section.documents).map((document) => document.id)),
            ...applicationRowIds,
          ]
        : [],
    );

    return {
      income,
      index,
      label: getIncomeSourceLabel(income, index),
      requestedRows,
      supportingDocuments: getDocuments(income.documents).filter(
        (document) => !document.id || !rowDocumentIds.has(document.id),
      ),
    };
  });

  const rows = isTemplate ? [...incomeSources.flatMap((group) => group.requestedRows), ...applicationRows] : [];

  return {
    mode: isTemplate ? 'template' : 'default',
    incomeSources,
    applicationRows: isTemplate ? applicationRows : [],
    noLongerRequested,
    requestedCount: rows.length,
    coveredCount: rows.filter((row) => row.documents.length > 0).length,
  };
}

export type PickedRow = { documentType: ApplicationConfigurationRequiredDocumentType; incomeId?: string };

export type PendingPick = PickedRow & { key: string; documentIds: Array<string> };

function isSameRow(section: RequiredDocumentSection, row: PickedRow) {
  return section.documentType === row.documentType && (section.incomeId || undefined) === row.incomeId;
}

/** The ids that the answers list on a row */
export function getRowDocumentIds(answers: IncomeDocumentsAnswers | null | undefined, row: PickedRow) {
  const section = (answers?.requiredDocuments ?? []).find((current) => !!current && isSameRow(current, row));
  return new Set((section?.documents ?? []).flatMap((document) => (document?.id ? [document.id] : [])));
}

/** Adds picked files to their row before the refetched application lists them */
export function withPendingPicks(
  answers: IncomeDocumentsAnswers | null | undefined,
  picks: Array<PendingPick>,
): IncomeDocumentsAnswers | null | undefined {
  if (!answers || picks.length === 0) {
    return answers;
  }
  return {
    currentIncomes: answers.currentIncomes,
    requiredDocuments: answers.requiredDocuments?.map((section) => {
      const listedIds = new Set((section.documents ?? []).map(({ id }) => id));
      const added = picks
        .filter((pick) => isSameRow(section, pick))
        .flatMap(({ documentIds }) => documentIds)
        .filter((id) => !listedIds.has(id));
      return added.length > 0
        ? { ...section, documents: [...(section.documents ?? []), ...added.map((id) => ({ id }))] }
        : section;
    }),
  };
}

function getTypedDocumentIds(model: IncomeDocumentsModel) {
  return new Set(
    [...model.incomeSources.flatMap(({ requestedRows }) => requestedRows), ...model.applicationRows].flatMap(
      ({ documents }) => documents.flatMap(({ id }) => (id ? [id] : [])),
    ),
  );
}

/**
 * The saved files a row can take: for an income-level row, the Supporting files of its source; for an
 * application-level row (no incomeId), the Supporting files of every source. Never a file on a visible typed row
 */
export function getPickerCandidates(model: IncomeDocumentsModel, incomeId: string | undefined): Array<Document> {
  const typedIds = getTypedDocumentIds(model);
  const supporting = incomeId
    ? (model.incomeSources.find(({ income }) => income.id === incomeId)?.supportingDocuments ?? [])
    : model.incomeSources.flatMap(({ supportingDocuments }) => supportingDocuments);
  const candidates = new Map<string, Document>();
  for (const document of supporting) {
    if (document.id && !typedIds.has(document.id) && !candidates.has(document.id)) {
      candidates.set(document.id, document);
    }
  }
  return [...candidates.values()];
}

export type PickableDocument = Document & { id: string; fileName: string };

// capps attach rejects the whole batch for an expired or unknown file, so only files with capps details qualify
export function getPickableDocuments(
  documents: Array<Document>,
  { now, isExcluded }: { now: number; isExcluded: (documentId: string) => boolean },
): Array<PickableDocument> {
  return documents.filter(
    (document): document is PickableDocument =>
      !!document.id &&
      !!document.fileName &&
      !isExcluded(document.id) &&
      !(document.expirationDateTime && new Date(document.expirationDateTime).valueOf() < now),
  );
}

// Lowercases the first letter unless the label starts with an acronym ("CPA attestation")
export function toTypePhrase(label: string) {
  return /^[A-Z][a-z]/.test(label) ? `${label[0].toLowerCase()}${label.slice(1)}` : label;
}
